import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { CheckCheckIcon, CompassIcon, FlagIcon, HelpCircleIcon, LayersIcon, RefreshCwIcon, UserIcon, XIcon } from 'lucide-react';
import { DeviceFrame } from '../DeviceFrame';
import { FormatToggle } from './FormatToggle';
import { signals, signalsClosing } from '../../data/deck';
import type { Format, SignalIcon } from '../../types/presentation';
interface SignalsOverlayProps {
  format: Format;
  onFormatChange: (format: Format) => void;
  onClose: () => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const ICONS: Record<SignalIcon, React.ComponentType<{
  className?: string;
}>> = {
  archetype: UserIcon,
  quiz: HelpCircleIcon,
  prior: CompassIcon,
  update: RefreshCwIcon,
  clusters: LayersIcon,
  completion: CheckCheckIcon,
  nextStep: FlagIcon
};

/** "What we learn" — the engagement signals the Snak captures. */
export function SignalsOverlay({
  format,
  onFormatChange,
  onClose
}: SignalsOverlayProps) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const compact = format === 'mobile';
  const content = <div className={compact ? 'bg-canvas px-6 pb-16 pt-12' : 'mx-auto max-w-[900px] px-6 pb-24 pt-16 lg:pt-24'} data-id="element-482" data-mp="39o2bhg6gq633">
      <h1 className={['max-w-[20ch] font-display font-semibold leading-[1] tracking-[-0.03em] text-navy', compact ? 'text-[30px]' : 'text-[42px] sm:text-[52px]'].join(' ')} data-id="element-483" data-mp="3ek9hta2rtjlk">
        Every answer creates a signal.
      </h1>

      <ul className={compact ? 'mt-10 grid gap-7' : 'mt-16 grid gap-x-12 gap-y-9 sm:grid-cols-2'} data-id="element-484" data-mp="1p6mgvef3yq5s">
        {signals.map((signal, i) => {
        const Icon = ICONS[signal.id];
        return <motion.li key={signal.id} initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.08 + i * 0.04,
          duration: 0.3,
          ease: EASE
        }} className="flex items-start gap-4" data-id="element-485" data-mp="3vv518wbvpbbs" data-mpi={i}>
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-paper text-navy ring-1 ring-navy/10" data-id="element-486" data-mp="3h60i9cx9f32m">
                <Icon className="h-4 w-4" aria-hidden="true" data-id="element-487" data-mp="3d2u31fs661k0" />
              </span>
              <span className="pt-0.5" data-id="element-488" data-mp="384smb1i7uebp">
                <span className="block font-display text-[16px] font-semibold leading-tight text-navy" data-id="element-489" data-mp="336r047phtyw0">
                  {signal.label}
                </span>
                <span className="mt-1.5 block max-w-[34ch] text-[12.5px] leading-relaxed text-navy/55" data-id="element-490" data-mp="2y9qutz0gx05y">
                  {signal.detail}
                </span>
              </span>
            </motion.li>;
      })}
      </ul>

      <p className={['border-t border-navy/[0.14] font-display font-semibold leading-[1.18] tracking-[-0.02em] text-navy', compact ? 'mt-12 max-w-[26ch] pt-8 text-[20px]' : 'mt-20 max-w-[38ch] pt-10 text-[26px]'].join(' ')} data-id="element-491" data-mp="14xtjsrdvyr0c">
        {signalsClosing}
      </p>
    </div>;
  return <motion.div role="dialog" aria-modal="true" aria-label="What we learn" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.25,
    ease: EASE
  }} className="no-print fixed inset-0 z-50 flex flex-col bg-canvas font-sans" data-id="element-492" data-mp="1inx2579na0ki">
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-navy/10 px-5 py-3 lg:px-10" data-id="element-493" data-mp="uxt034c3u8vm">
        <span className="truncate text-[10px] uppercase tracking-[0.24em] text-navy/45" data-id="element-494" data-mp="zwe0p4v9qukl">What we learn</span>
        <div className="flex shrink-0 items-center gap-2" data-id="element-495" data-mp="26gx1cwb7kcws">
          <FormatToggle value={format} onChange={onFormatChange} data-id="element-496" data-mp="2bccchghtzupi" />
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex h-9 items-center gap-2 rounded-full border border-navy/20 px-4 text-[10px] uppercase tracking-[0.16em] text-navy transition-colors duration-150 hover:border-navy/50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue" data-id="element-497" data-mp="3ka2fbf6bhbr7">
            <XIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-498" data-mp="742cx88uytvl" />
            <span className="hidden sm:inline" data-id="element-499" data-mp="727rlstoczqz">Close</span>
          </button>
        </div>
      </div>

      {compact ? <div className="flex flex-1 items-center justify-center overflow-hidden px-4 py-5" data-id="element-500" data-mp="234v4wrrg1um">
          <DeviceFrame label="What we learn in phone format" sizeClassName="h-[clamp(500px,calc(100vh-136px),860px)] w-auto aspect-[344/688]" data-id="element-501" data-mp="fygtde54kaga">
            {content}
          </DeviceFrame>
        </div> : <div className="flex-1 overflow-y-auto" data-id="element-502" data-mp="kw8k44u19qiw">{content}</div>}
    </motion.div>;
}